import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import type { SmartGuideLine } from './types/smart-guide-line'
import type { SmartGuideResult } from './types/smart-guide-result'
import type { LayoutSnapLines } from './types/layout-snap-lines'
import { edgesOf } from './helpers/edges-of'
import { detectDistributionAlignment } from './helpers/distribution-alignment'

interface EdgeMatch {
  type: 'horizontal' | 'vertical'
  position: number
  adjust: number
  /** Cross-axis coordinates used to compute the guide line extent */
  crossMin: number
  crossMax: number
}

/** Dedupe positions from static guides (margins, columns, ruler lines) for smart-guide alignment. */
export function extractLayoutSnapLines(
  guides: ReadonlyArray<{ type: 'horizontal' | 'vertical'; position: number }>
): LayoutSnapLines {
  const vertical: number[] = []
  const horizontal: number[] = []
  const seenV = new Set<number>()
  const seenH = new Set<number>()

  for (const g of guides) {
    const p = g.position

    if (!Number.isFinite(p)) continue
    const key = Math.round(p * 1000)

    if (g.type === 'vertical') {
      if (!seenV.has(key)) {
        seenV.add(key)
        vertical.push(p)
      }
    } else if (!seenH.has(key)) {
      seenH.add(key)
      horizontal.push(p)
    }
  }

  return { vertical, horizontal }
}

/**
 * Compute smart guide lines and snap adjustments for a moving/resizing shape
 * relative to all other shapes on the canvas and optional layout lines (margins, columns, etc.).
 */
export function computeSmartGuides(
  movingBounds: ObjectBounds,
  otherBounds: ObjectBounds[],
  threshold: number,
  layoutLines?: LayoutSnapLines
): SmartGuideResult {
  const hasLayout =
    (layoutLines?.vertical?.length ?? 0) > 0 || (layoutLines?.horizontal?.length ?? 0) > 0

  if (otherBounds.length === 0 && !hasLayout) {
    return { guides: [], snapAdjust: { dx: 0, dy: 0 } }
  }

  const moving = edgesOf(movingBounds)
  const verticalMatches: EdgeMatch[] = []
  const horizontalMatches: EdgeMatch[] = []

  for (const other of otherBounds) {
    const o = edgesOf(other)

    const vCandidates: Array<{ movingEdge: number; otherEdge: number }> = [
      { movingEdge: moving.left, otherEdge: o.left },
      { movingEdge: moving.left, otherEdge: o.right },
      { movingEdge: moving.left, otherEdge: o.centerX },
      { movingEdge: moving.right, otherEdge: o.left },
      { movingEdge: moving.right, otherEdge: o.right },
      { movingEdge: moving.right, otherEdge: o.centerX },
      { movingEdge: moving.centerX, otherEdge: o.left },
      { movingEdge: moving.centerX, otherEdge: o.right },
      { movingEdge: moving.centerX, otherEdge: o.centerX },
    ]

    for (const c of vCandidates) {
      const diff = c.otherEdge - c.movingEdge

      if (Math.abs(diff) <= threshold) {
        verticalMatches.push({
          type: 'vertical',
          position: c.otherEdge,
          adjust: diff,
          crossMin: Math.min(moving.top, moving.bottom, o.top, o.bottom),
          crossMax: Math.max(moving.top, moving.bottom, o.top, o.bottom),
        })
      }
    }

    const hCandidates: Array<{ movingEdge: number; otherEdge: number }> = [
      { movingEdge: moving.top, otherEdge: o.top },
      { movingEdge: moving.top, otherEdge: o.bottom },
      { movingEdge: moving.top, otherEdge: o.centerY },
      { movingEdge: moving.bottom, otherEdge: o.top },
      { movingEdge: moving.bottom, otherEdge: o.bottom },
      { movingEdge: moving.bottom, otherEdge: o.centerY },
      { movingEdge: moving.centerY, otherEdge: o.top },
      { movingEdge: moving.centerY, otherEdge: o.bottom },
      { movingEdge: moving.centerY, otherEdge: o.centerY },
    ]

    for (const c of hCandidates) {
      const diff = c.otherEdge - c.movingEdge

      if (Math.abs(diff) <= threshold) {
        horizontalMatches.push({
          type: 'horizontal',
          position: c.otherEdge,
          adjust: diff,
          crossMin: Math.min(moving.left, moving.right, o.left, o.right),
          crossMax: Math.max(moving.left, moving.right, o.left, o.right),
        })
      }
    }
  }

  for (const gx of layoutLines?.vertical ?? []) {
    if (!Number.isFinite(gx)) continue

    for (const movingEdge of [moving.left, moving.right, moving.centerX]) {
      const diff = gx - movingEdge

      if (Math.abs(diff) <= threshold) {
        verticalMatches.push({
          type: 'vertical',
          position: gx,
          adjust: diff,
          crossMin: moving.top,
          crossMax: moving.bottom,
        })
      }
    }
  }

  for (const gy of layoutLines?.horizontal ?? []) {
    if (!Number.isFinite(gy)) continue

    for (const movingEdge of [moving.top, moving.bottom, moving.centerY]) {
      const diff = gy - movingEdge

      if (Math.abs(diff) <= threshold) {
        horizontalMatches.push({
          type: 'horizontal',
          position: gy,
          adjust: diff,
          crossMin: moving.left,
          crossMax: moving.right,
        })
      }
    }
  }

  let bestDx = 0
  let bestDy = 0
  let bestAbsDx = Infinity
  let bestAbsDy = Infinity

  for (const m of verticalMatches) {
    const abs = Math.abs(m.adjust)

    if (abs < bestAbsDx) {
      bestAbsDx = abs
      bestDx = m.adjust
    }
  }

  for (const m of horizontalMatches) {
    const abs = Math.abs(m.adjust)

    if (abs < bestAbsDy) {
      bestAbsDy = abs
      bestDy = m.adjust
    }
  }

  const guides: SmartGuideLine[] = []
  const seenGuides = new Set<string>()

  const addGuide = (type: SmartGuideLine['type'], position: number, from: number, to: number) => {
    const key = `${type}:${Math.round(position * 100)}`

    if (seenGuides.has(key)) {
      const existing = guides.find((g) => g.type === type && Math.abs(g.position - position) < 0.01)

      if (existing) {
        existing.from = Math.min(existing.from, from)
        existing.to = Math.max(existing.to, to)
      }

      return
    }

    seenGuides.add(key)
    guides.push({ type, position, from, to })
  }

  for (const m of verticalMatches) {
    if (Math.abs(m.adjust - bestDx) < 0.01) {
      addGuide('vertical', m.position, m.crossMin, m.crossMax)
    }
  }

  for (const m of horizontalMatches) {
    if (Math.abs(m.adjust - bestDy) < 0.01) {
      addGuide('horizontal', m.position, m.crossMin, m.crossMax)
    }
  }

  const distribution = detectDistributionAlignment(movingBounds, otherBounds, threshold)

  for (const dg of distribution) {
    addGuide(dg.type, dg.position, dg.from, dg.to)
  }

  return {
    guides,
    snapAdjust: { dx: bestDx, dy: bestDy },
  }
}
